/*
  section-cocktail-makers-social-proof.js
  UGC video modal for the social proof section.

  Markup contract:
  - [data-ugc-gallery]       wraps the tiles and the dialog
  - [data-ugc-open]          tile button; data-poster (large poster URL), data-product (handle)
                             its <li> also holds a <template data-ugc-video> with the video
                             and, optionally, the featured product link
  - [data-ugc-modal]         <dialog>; data-auto-advance="true|false"
  - [data-ugc-card]          the 9:16 video card (tap, swipe)
  - [data-ugc-stage]         where the active video + product card are inserted
  - [data-ugc-progress]      playback progress bar fill
  - [data-ugc-seek]          progress hit area; click, drag or arrow keys to seek (role="slider")
  - [data-ugc-mute]          mute toggle
  - [data-ugc-close]         close button
  - [data-ugc-step]          previous (-1) / next (1)
  - [data-ugc-count]         "2 / 8"
  - [data-ugc-flash]         play/pause feedback icon
  - [data-ugc-loading]       buffering indicator

  Behaviour:
  - Native <dialog>: Escape closes, focus stays inside, focus returns to the tile.
  - Starts with sound (opening is a user tap). If the browser refuses, falls back to muted.
    The mute choice carries across videos.
  - Tap the video to pause/play. Swipe left/right to step, swipe down to close (touch).
  - Arrow keys step, space toggles play, M toggles mute.
  - Click or drag the progress bar to seek; with it focused, arrow keys move 5s.
  - Auto-advances on end (not with reduced motion, not past the last video).
  - Only the active video is in the page; it's removed on step and close.
  - The next video's poster is preloaded so stepping feels instant.

  Analytics: dispatches on document, for GTM or any listener
  - ugc:open          { section, position, total, product }
  - ugc:complete      { section, position, total, product }
  - ugc:product_click { section, position, total, product }
*/
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SWIPE_DISTANCE = 50;
  const DISMISS_DISTANCE = 90;
  const TAP_TOLERANCE = 10;

  function initUgcGallery(root) {
    if (root.dataset.ugcInit === 'true') return;
    root.dataset.ugcInit = 'true';

    const dialog = root.querySelector('[data-ugc-modal]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const card = dialog.querySelector('[data-ugc-card]');
    const stage = dialog.querySelector('[data-ugc-stage]');
    const progress = dialog.querySelector('[data-ugc-progress]');
    const seekBar = dialog.querySelector('[data-ugc-seek]');
    const muteButton = dialog.querySelector('[data-ugc-mute]');
    const count = dialog.querySelector('[data-ugc-count]');
    const flash = dialog.querySelector('[data-ugc-flash]');
    const loading = dialog.querySelector('[data-ugc-loading]');
    const prev = dialog.querySelector('[data-ugc-step="-1"]');
    const next = dialog.querySelector('[data-ugc-step="1"]');

    const tiles = Array.from(root.querySelectorAll('[data-ugc-open]'));
    const templates = tiles.map(tile => tile.parentElement.querySelector('template[data-ugc-video]'));
    const sectionId = root.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || '';
    const autoAdvance = dialog.dataset.autoAdvance !== 'false';

    let current = 0;
    let video = null;
    let muted = false;

    /* ---------- analytics ---------- */
    function track(name) {
      document.dispatchEvent(new CustomEvent(name, {
        detail: {
          section: sectionId,
          position: current + 1,
          total: tiles.length,
          product: tiles[current]?.dataset.product || null
        }
      }));
    }

    /* ---------- UI state ---------- */
    function setMuted(value) {
      muted = value;
      if (video) video.muted = muted;
      muteButton?.setAttribute('aria-pressed', String(muted));
      muteButton?.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    }

    function setLoading(isLoading) {
      if (loading) loading.hidden = !isLoading;
    }

    function showFlash(state) {
      if (!flash) return;
      flash.dataset.state = state;
      flash.classList.remove('is-visible');
      void flash.offsetWidth; // restart the animation
      flash.classList.add('is-visible');
    }

    function formatTime(seconds) {
      const total = Math.max(0, Math.floor(seconds || 0));
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    }

    function updateProgress() {
      if (!progress || !video || !video.duration) return;
      const ratio = video.currentTime / video.duration;
      progress.style.transform = `scaleX(${ratio})`;
      if (seekBar) {
        seekBar.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
        seekBar.setAttribute('aria-valuetext', `${formatTime(video.currentTime)} of ${formatTime(video.duration)}`);
      }
    }

    function seekToPointer(e) {
      if (!video || !video.duration || !seekBar) return;
      const track = seekBar.querySelector('.ugc-modal__progress-track') || seekBar;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      video.currentTime = ratio * video.duration;
      updateProgress();
    }

    function preloadPoster(index) {
      const url = tiles[index]?.dataset.poster;
      if (url) new Image().src = url;
    }

    /* ---------- playback ---------- */
    function play() {
      if (!video) return;
      video.muted = muted;
      const attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(error => {
          // Only fall back to muted when the browser blocked sound, not for other failures
          if (error && error.name === 'NotAllowedError' && video && !video.muted) {
            setMuted(true);
            video.play().catch(() => {});
          }
        });
      }
    }

    function togglePlay() {
      if (!video) return;
      if (video.paused || video.ended) {
        if (video.ended) video.currentTime = 0;
        play();
        showFlash('play');
      } else {
        video.pause();
        showFlash('pause');
      }
    }

    function show(index) {
      if (index < 0 || index > templates.length - 1) return;
      current = index;

      stage.replaceChildren();
      if (progress) progress.style.transform = 'scaleX(0)';
      setLoading(true);

      const template = templates[current];
      if (template) stage.appendChild(template.content.cloneNode(true));
      video = stage.querySelector('video');

      if (count) count.textContent = tiles.length > 1 ? `${current + 1} / ${tiles.length}` : '';
      if (prev) { prev.disabled = current === 0; prev.hidden = tiles.length < 2; }
      if (next) { next.disabled = current === tiles.length - 1; next.hidden = tiles.length < 2; }

      stage.querySelector('[data-ugc-product-link]')?.addEventListener('click', () => track('ugc:product_click'));

      if (video) {
        video.addEventListener('timeupdate', updateProgress);
        video.addEventListener('waiting', () => setLoading(true));
        video.addEventListener('playing', () => setLoading(false));
        video.addEventListener('canplay', () => setLoading(false));
        video.addEventListener('ended', () => {
          track('ugc:complete');
          if (progress) progress.style.transform = 'scaleX(1)';
          if (autoAdvance && !reduceMotion.matches && current < tiles.length - 1) show(current + 1);
        });
        play();
      } else {
        setLoading(false);
      }

      preloadPoster(current + 1);
    }

    function open(index) {
      dialog.showModal();
      show(index);
      track('ugc:open');
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    /* ---------- events ---------- */
    tiles.forEach((tile, index) => tile.addEventListener('click', () => open(index)));

    dialog.querySelectorAll('[data-ugc-step]').forEach(button => {
      button.addEventListener('click', () => show(current + Number(button.dataset.ugcStep)));
    });

    dialog.querySelector('[data-ugc-close]')?.addEventListener('click', close);
    muteButton?.addEventListener('click', () => setMuted(!muted));

    // Anywhere outside the card and the buttons closes it
    dialog.addEventListener('click', e => {
      if (!e.target.closest('[data-ugc-card], button')) close();
    });

    // Scrubbing: click to jump, drag to scrub. Stops the card's tap/swipe handling.
    let seeking = false;
    let resumeAfterSeek = false;

    seekBar?.addEventListener('pointerdown', e => {
      if (!video) return;
      e.stopPropagation();
      seeking = true;
      resumeAfterSeek = !video.paused;
      video.pause();
      seekBar.classList.add('is-seeking');
      seekBar.setPointerCapture?.(e.pointerId);
      seekToPointer(e);
    });

    seekBar?.addEventListener('pointermove', e => {
      if (seeking) seekToPointer(e);
    });

    function endSeek() {
      if (!seeking) return;
      seeking = false;
      seekBar.classList.remove('is-seeking');
      if (resumeAfterSeek) play();
    }
    seekBar?.addEventListener('pointerup', e => { e.stopPropagation(); endSeek(); });
    seekBar?.addEventListener('pointercancel', endSeek);
    seekBar?.addEventListener('lostpointercapture', endSeek);

    seekBar?.addEventListener('keydown', e => {
      if (!video || !video.duration) return;
      const steps = { ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5 };
      if (e.key in steps) {
        video.currentTime = Math.min(video.duration, Math.max(0, video.currentTime + steps[e.key]));
      } else if (e.key === 'Home') {
        video.currentTime = 0;
      } else if (e.key === 'End') {
        video.currentTime = video.duration;
      } else {
        return;
      }
      e.preventDefault();
      e.stopPropagation(); // don't also step to another video
      updateProgress();
    });

    dialog.addEventListener('keydown', e => {
      if (e.target.closest('a')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1); }
      else if (e.key === ' ' && !e.target.closest('button')) { e.preventDefault(); togglePlay(); }
      else if (e.key === 'm' || e.key === 'M') { setMuted(!muted); }
    });

    // Tap to pause/play, swipe to step, swipe down to close.
    // Taps on the product link and control buttons are left alone.
    let startX = 0;
    let startY = 0;
    let tracking = false;

    card?.addEventListener('pointerdown', e => {
      if (e.target.closest('a, button, [data-ugc-seek]')) return;
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    });

    card?.addEventListener('pointerup', e => {
      if (!tracking) return;
      tracking = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (Math.abs(dx) < TAP_TOLERANCE && Math.abs(dy) < TAP_TOLERANCE) {
        togglePlay();
      } else if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
        show(current + (dx < 0 ? 1 : -1));
      } else if (dy > DISMISS_DISTANCE && dy > Math.abs(dx)) {
        close();
      }
    });

    card?.addEventListener('pointercancel', () => { tracking = false; });

    // Remove the video on close so playback and downloading stop
    dialog.addEventListener('close', () => {
      stage.replaceChildren();
      video = null;
      setLoading(false);
    });

    setMuted(false);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ugc-gallery]').forEach(initUgcGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => initAll(e.target));

  window.UgcGallery = { init: initUgcGallery, initAll };
})();
